import React, { useMemo } from 'react';
import { generateJSON } from '@tiptap/core';
import { useFormikContext } from 'formik';
import { isEqual } from 'lodash';

import { getExtensions } from '~/components/core/Editor/extensions/config';
import BaseGenericTemplatesEditor from '~/components/GenericTemplates/GenericTemplatesConfiguration/GenericTemplateBuilder/BaseGenericTemplatesEditor';
import type { TemplateBuilderEditorInnerComponentProps } from '~/components/GenericTemplates/types';
import { TEMPLATE_BUILDER_FORM_KEYS } from '~/components/GenericTemplates/utils/genericTemplatesUtils';
import useNotesConfiguration from '~/components/hooks/useNotesConfiguration';
import { RICH_NOTE_TEMPLATE_DISPLAY_CONFIG } from '~/components/Notes/richText';

// Whether the plain builder would drop some of this body's formatting: it lacks extensions the rich
// builder has, and the first edit would save the stripped body.
const hasRichOnlyFormatting = (html: string) =>
  !isEqual(
    generateJSON(html, getExtensions([], {})),
    generateJSON(html, getExtensions([], RICH_NOTE_TEMPLATE_DISPLAY_CONFIG))
  );

const NoteGenericTemplatesEditor: React.FC<TemplateBuilderEditorInnerComponentProps> = ({ disabled, suggestions }) => {
  const { configuration: notesConfiguration } = useNotesConfiguration(true);
  const { initialValues } = useFormikContext<Record<string, string>>();
  const initialBody = initialValues[TEMPLATE_BUILDER_FORM_KEYS.BODY_TEMPLATE];
  // A template formatted while rich text notes were on keeps its formatting after they are turned off.
  const isStoredBodyRich = useMemo(() => !!initialBody && hasRichOnlyFormatting(initialBody), [initialBody]);
  const isRichText = !!notesConfiguration?.is_rich_text_notes_enabled || isStoredBodyRich;

  return (
    <BaseGenericTemplatesEditor
      // The editor registers its formatting extensions when it mounts, so remount it once the
      // configuration has loaded.
      key={isRichText ? 'rich' : 'plain'}
      titleHeading="Note Title"
      bodyHeading="Note Body"
      suggestions={suggestions}
      disabled={disabled}
      bodyFormatDisplayConfig={isRichText ? RICH_NOTE_TEMPLATE_DISPLAY_CONFIG : undefined}
    />
  );
};

export default NoteGenericTemplatesEditor;
